/**
 * utils/sessionAnalytics.js
 */

/**
 * -----------------------------------------
 * FILTER HELPERS
 * -----------------------------------------
 */

export const getSessionEvents = (
  events
) =>
  events.filter(
    (e) =>
      e.event === "session_started" ||
      e.event === "session_ended"
  );

export const getViewEvents = (events) =>
  events.filter(
    (e) => e.event === "view_changed"
  );

  export const getFilterEvents = (
    events
  ) =>
    events.filter(
      (e) =>
        e.event ===
          "filter_opened" ||
        e.event ===
          "filter_location_changed" ||
        e.event ===
          "filter_budget_changed" ||
        e.event ===
          "filter_closed"
    );

export const getPropertyEvents = (
  events
) =>
  events.filter(
    (e) => e.event === "property_viewed"
  );

/**
 * -----------------------------------------
 * SESSION DURATION
 * -----------------------------------------
 */

export const calculateSessionDuration = (
  sessionEvents
) => {
  const sessionStart = sessionEvents.find(
    (e) => e.event === "session_started"
  );

  const sessionEnd = sessionEvents.find(
    (e) => e.event === "session_ended"
  );

  if (!sessionStart || !sessionEnd) {
    return null;
  }

  return {
    startedAt: sessionStart.timestamp,

    endedAt: sessionEnd.timestamp,

    durationSeconds: msToSeconds(
      toMs(sessionEnd.timestamp) -
        toMs(sessionStart.timestamp)
    ),
  };
};

/**
 * -----------------------------------------
 * VIEW DURATIONS
 * -----------------------------------------
 */

export const calculateViewDurations = ({
  viewEvents,
  sessionEvents,
}) => {
  const sessionStart = sessionEvents.find(
    (e) => e.event === "session_started"
  );

  const sessionEnd = sessionEvents.find(
    (e) => e.event === "session_ended"
  );

  const timeline = [
    {
      event: "view_changed",
      data: { view: "chat" },
      timestamp: sessionStart.timestamp,
    },

    ...viewEvents,

    {
      event: "session_ended",
      timestamp: sessionEnd.timestamp,
    },
  ];

  return timeline
    .map((current, index) => {
      const next = timeline[index + 1];

      if (!next || !current.data?.view) {
        return null;
      }

      return {
        view: current.data.view,

        startedAt: current.timestamp,

        endedAt: next.timestamp,

        durationSeconds: msToSeconds(
          toMs(next.timestamp) -
            toMs(current.timestamp)
        ),
      };
    })
    .filter(Boolean);
};

/**
 * -----------------------------------------
 * FILTER DURATIONS
 * -----------------------------------------
 */

const toMs = (timestamp) =>
    new Date(timestamp).getTime();
  
  const msToSeconds = (ms) =>
    Math.floor(ms / 1000);
  
  /**
   * -----------------------------------------
   * FILTER INTERACTION ANALYTICS
   * -----------------------------------------
   */
  
  export const calculateFilterAnalytics = (
    filterEvents
  ) => {
    const filterSessions = [];
  
    /**
     * Active filter state
     */
  
    const activeFilters = {};
  
    filterEvents.forEach(
      (event, index) => {
        /**
         * -----------------------------
         * FILTER OPENED
         * -----------------------------
         */
  
        if (
          event.event === "filter_opened"
        ) {
          const filterName =
            event.data.filter;
  
          activeFilters[filterName] = {
            filter: filterName,
  
            openedAt:
              event.timestamp,
  
            closedAt: null,
  
            durationSeconds: 0,
  
            interactions: [],
          };
        }
  
        /**
         * -----------------------------
         * LOCATION CHANGE
         * -----------------------------
         */
  
        if (
          event.event ===
          "filter_location_changed"
        ) {
          const filter =
            activeFilters.location;
  
          if (!filter) return;
  
          filter.interactions.push({
            type: "location_selected",
  
            value:
              event.data.location,
  
            timestamp:
              event.timestamp,
          });
        }
  
        /**
         * -----------------------------
         * BUDGET CHANGE
         * -----------------------------
         */
  
        if (
          event.event ===
          "filter_budget_changed"
        ) {
          const filter =
            activeFilters.budget ||
            activeFilters.location;
  
          if (!filter) return;
  
          filter.interactions.push({
            type: "budget_changed",
  
            budgetMin:
              event.data.budget_min,
  
            budgetMax:
              event.data.budget_max,
  
            timestamp:
              event.timestamp,
          });
        }
  
        /**
         * -----------------------------
         * FILTER CLOSED
         * -----------------------------
         */
  
        if (
          event.event === "filter_closed"
        ) {
          const filterName =
            event.data.filter;
  
          const filter =
            activeFilters[filterName];
  
          if (!filter) return;
  
          filter.closedAt =
            event.timestamp;
  
          filter.durationSeconds =
            msToSeconds(
              toMs(event.timestamp) -
                toMs(
                  filter.openedAt
                )
            );
  
          filterSessions.push(filter);
  
          delete activeFilters[
            filterName
          ];
        }
      }
    );
  
    return filterSessions;
  };
/**
 * -----------------------------------------
 * PROPERTY VIEWS
 * -----------------------------------------
 */

export const calculatePropertyViews = (
  propertyEvents
) => {
  return propertyEvents.map((event) => ({
    property:
      event.data?.property || "Unknown",

    viewedAt: event.timestamp,
  }));
};

/**
 * -----------------------------------------
 * MAIN ANALYTICS
 * -----------------------------------------
 */

export const generateLeadAnalytics = (
  events
) => {
  /**
   * Split by event type first
   */

  const sessionEvents =
    getSessionEvents(events);

  const viewEvents =
    getViewEvents(events);

  const filterEvents =
    getFilterEvents(events);

  const propertyEvents =
    getPropertyEvents(events);

  /**
   * Pass only relevant data
   */

  const session =
    calculateSessionDuration(
      sessionEvents
    );

  const views =
    calculateViewDurations({
      viewEvents,
      sessionEvents,
    });

    const filterAnalytics =
    calculateFilterAnalytics(
      filterEvents  
    );

  const filters = filterAnalytics;

  const properties =
    calculatePropertyViews(
      propertyEvents
    );

  /**
   * Totals
   */

  const totals = views.reduce(
    (acc, item) => {
      acc[item.view] =
        (acc[item.view] || 0) +
        item.durationSeconds;

      return acc;
    },
    {}
  );

  return {
    session,

    views,

    filters,

    properties,

    totals: {
      totalChatTime:
        totals.chat || 0,

      totalProjectsTime:
        totals.projects || 0,
    },
  };
};


